import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import type { ItemDTO, Kind } from "@barkup/shared";
import { deleteItems, downloadItem, isAbortError, listItems, restoreItems } from "../api.js";
import { compareItems, filterByQuery, normalizeQuery } from "../search.js";
import { dayEndISO, dayStartISO } from "../format.js";
import { store } from "../store.js";

/**
 * The top-level tabs. "MEDIA" folds images and videos together (both are
 * thumbnail-first and nameless, so they share the square grid); files and links
 * each get their own list. There is no "all" tab — the archive always shows one
 * category, MEDIA by default.
 */
export type Category = "MEDIA" | "FILE" | "LINK";

const CATEGORY_KINDS: Record<Category, Kind[]> = {
  MEDIA: ["IMAGE", "VIDEO"],
  FILE: ["FILE"],
  LINK: ["LINK"],
};

/**
 * How long after the last keystroke the server query fires.
 *
 * Deliberately generous. Because the local pass paints matching results on the
 * keystroke itself, this delay is invisible — it buys fewer requests on mobile
 * data without the user ever waiting on it. It would be far too slow if the
 * server round-trip were the only thing feeding the list, which is exactly what
 * it used to be.
 */
const SEARCH_DEBOUNCE_MS = 220;

/**
 * Whether what's on screen came from the local index or from the server.
 *
 * `local` means "a correct subset, possibly incomplete" — the archive is larger
 * than what's been paged in, so a match may exist that this device has never
 * seen. The UI must not present a local result set as a complete answer, and
 * must not paginate from it (the cursor still belongs to the previous query).
 */
export type ResultScope = "local" | "server";

/**
 * What the list area should be rendering. Derived in one place because both the
 * archive and the trash view need exactly the same six-way decision, and the
 * distinctions are easy to get subtly wrong — "no files yet" and "nothing
 * matched your search" used to be the same branch, so searching an archive for
 * a word it didn't contain told the user their archive was empty.
 */
export type ListState =
  | "skeleton"
  | "error"
  | "searching"
  | "no-results"
  | "filtered-empty"
  | "empty"
  | "ready";

/**
 * Shared list/filter/select/pagination logic for the live archive view and
 * the trash view — they differ only in `trash: true/false` and which
 * action (delete vs restore) their buttons trigger.
 */
export function useItemList(opts: { trash: boolean }) {
  const items = ref<ItemDTO[]>([]);
  /**
   * Every item seen under the current scope (archive + trash + category + date
   * range), held in the server's own order — the index the instant search pass
   * reads. Rebuilt whenever the scope changes, extended as pages load, and
   * pruned explicitly when a mutation removes items, since a filtered reload
   * can't tell "gone" apart from "didn't match".
   */
  const pool = ref<ItemDTO[]>([]);
  const nextCursor = ref<string | null>(null);
  const loading = ref(false);
  const resultScope = ref<ResultScope>("server");
  /**
   * Skeleton rows are for a load that has nothing to show yet — the first open
   * and a scope change, which deliberately blank the list. A search must never
   * raise it: the local pass has already painted, and swapping real rows for
   * skeletons on a keystroke is precisely the flicker this design removes.
   */
  const showSkeleton = ref(true);
  /**
   * A server search is owed for what's currently typed — either the debounce
   * timer is still running or the request is in flight. Drives the field's
   * in-flight affordance and, more importantly, stops the views from declaring
   * "nothing matched" while the authoritative answer is still coming.
   */
  const searchPending = ref(false);
  /**
   * A failed *initial* fetch (reset load). Kept separate from the action
   * `errorLabel` toast: without it, a first-page load that threw left the view
   * showing the "no files yet" empty state — indistinguishable from a genuinely
   * empty archive. That was the 1:1-launch bug: the very first `listItems`
   * after LIFF init could fail, and nothing surfaced it, so the archive looked
   * empty until a tab switch remounted the view and retried. Now the view shows
   * a retry instead.
   */
  const loadError = ref<string | null>(null);
  const selected = reactive(new Set<string>());
  /**
   * Drive-style selection: OFF by default (rows carry no checkboxes), entered
   * by long-pressing an item or via the ⋮ sheet's "เลือกหลายรายการ", exited
   * when the last item is deselected or the bar's ✕ is tapped.
   */
  const selectionMode = ref(false);

  /**
   * In-flight label for any mutating/API action ("กำลังลบ..." etc.) — views
   * render it in a BusyToast, and its truthiness disables action buttons.
   * One mutation at a time: a second call while busy is dropped, which is
   * also the double-tap guard.
   */
  const busyLabel = ref<string | null>(null);
  const errorLabel = ref<string | null>(null);
  let errorTimer: number | undefined;

  async function runBusy(label: string, fn: () => Promise<void>): Promise<void> {
    if (busyLabel.value) return;
    busyLabel.value = label;
    errorLabel.value = null;
    try {
      await fn();
    } catch {
      // The action didn't happen — say so briefly instead of failing silently.
      errorLabel.value = "ทำรายการไม่สำเร็จ ลองอีกครั้ง";
      clearTimeout(errorTimer);
      errorTimer = window.setTimeout(() => (errorLabel.value = null), 3000);
    } finally {
      busyLabel.value = null;
    }
  }

  const filters = reactive({
    category: "MEDIA" as Category,
    dateFrom: "",
    dateTo: "",
    q: "",
  });

  /**
   * The string actually searched with — trimmed. `filters.q` stays exactly what
   * the user typed (it's the input's value); everything downstream, local pass
   * and outgoing request alike, uses this one derivation so the two can't drift.
   */
  const query = computed(() => normalizeQuery(filters.q));

  // View follows the category, not a manual toggle: media is a square grid
  // (nameless thumbnails), files and links are lists (their name is the point).
  const view = computed<"list" | "grid">(() => (filters.category === "MEDIA" ? "grid" : "list"));

  /**
   * Request generation. Only the newest request may touch state — anything
   * older is a superseded query whose answer is, by definition, wrong for
   * what's typed now. This is what the old per-keystroke code lacked: responses
   * could land out of order and the list would settle on an earlier query's
   * results.
   */
  let seq = 0;
  let inFlight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  function mergeIntoPool(incoming: ItemDTO[]): void {
    const byId = new Map(pool.value.map((item) => [item.id, item]));
    for (const item of incoming) byId.set(item.id, item);
    pool.value = [...byId.values()].sort(compareItems);
  }

  /** Mutations are the one thing a filtered reload can't teach the index. */
  function dropFromPool(ids: string[]): void {
    const gone = new Set(ids);
    pool.value = pool.value.filter((item) => !gone.has(item.id));
  }

  function cancelPendingSearch(): void {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    searchPending.value = false;
  }

  async function load(reset: boolean) {
    if (!store.archive) {
      // Nothing to query yet. Release the in-flight affordance rather than
      // leaving the search field spinning forever on a promise never made.
      searchPending.value = false;
      return;
    }
    const mySeq = ++seq;
    inFlight?.abort(); // whatever was running answers a question nobody is asking any more
    const controller = new AbortController();
    inFlight = controller;
    const q = query.value;

    loading.value = true;
    if (reset) loadError.value = null;
    try {
      const res = await listItems(
        {
          archiveId: store.archive.id,
          trash: opts.trash,
          kinds: CATEGORY_KINDS[filters.category],
          // Whole local days, not UTC midnights — see dayStartISO in format.ts.
          dateFrom: dayStartISO(filters.dateFrom),
          dateTo: dayEndISO(filters.dateTo),
          q: q || undefined,
          cursor: reset ? undefined : (nextCursor.value ?? undefined),
        },
        controller.signal
      );
      if (mySeq !== seq) return;

      if (reset) {
        items.value = res.items;
        // An unfiltered first page is the authoritative head of this scope, so
        // it replaces the index outright — that's how a deleted item stops
        // haunting local results. A *filtered* page can only add to the index;
        // absence from it means "didn't match", not "no longer exists".
        if (q) mergeIntoPool(res.items);
        else pool.value = [...res.items];
      } else {
        items.value = [...items.value, ...res.items];
        mergeIntoPool(res.items);
      }
      nextCursor.value = res.nextCursor;
      resultScope.value = "server";
    } catch (err) {
      if (isAbortError(err) || mySeq !== seq) return;
      // Only the first page's failure needs a visible surface; a failed
      // "load more" just leaves the existing list intact and the sentinel
      // will retry on the next scroll.
      if (reset) loadError.value = "โหลดรายการไม่สำเร็จ";
    } finally {
      if (mySeq === seq) {
        loading.value = false;
        showSkeleton.value = false;
        inFlight = null;
        if (!debounceTimer) searchPending.value = false;
      }
    }
  }

  // Reload when a discrete filter (tab / date range) or the active archive
  // changes (e.g. switching into a group's archive from GroupManagementView).
  // The list is blanked first so the skeleton shows while the new result set
  // loads — switching a tab replaces the whole list, so a moment of loading is
  // the honest state, not the stale previous tab sitting there. The index goes
  // with it: items from the files tab must not surface in a search on the media
  // tab. NOT immediate — the initial load is fired from onMounted below so it
  // runs once the component is mounted with the resolved archive, rather than
  // mid-setup where the archive may not be readable yet (the first-open
  // empty-list bug).
  watch(
    () => [filters.category, filters.dateFrom, filters.dateTo, store.archive?.id],
    () => {
      cancelSelection();
      cancelPendingSearch();
      items.value = [];
      pool.value = [];
      nextCursor.value = null;
      resultScope.value = "server";
      showSkeleton.value = true;
      void load(true);
    }
  );

  /**
   * Search runs in two passes, and the split is the entire point.
   *
   * Pass one is synchronous: filter the already-loaded index and paint it now,
   * on the keystroke, with no network involved. Because the local predicate
   * mirrors the server's exactly (see search.ts), those rows are guaranteed to
   * survive into the real answer — so they can be shown immediately without any
   * risk of having to retract them.
   *
   * Pass two is the debounced server query, which fills in everything this
   * device hasn't paged in. It lands in the same date order, so it inserts rows
   * rather than reshuffling the ones already on screen.
   *
   * The list is never blanked between the two. Flashing a skeleton on every
   * character typed is the flicker this design exists to remove.
   */
  watch(query, (q) => {
    cancelSelection();
    // A previous query's server failure says nothing about this one. Left set,
    // it would brand the very first keystroke of a fresh search as failed,
    // before its request had even been made.
    loadError.value = null;
    items.value = filterByQuery(pool.value, q);
    // The local pass is only provably complete in one case: no query, and the
    // whole scope already paged in — then the index *is* the archive. Any other
    // time it's a correct subset, and the UI is told so rather than presenting
    // it as the final answer.
    resultScope.value = !q && nextCursor.value === null ? "server" : "local";

    searchPending.value = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void load(true);
    }, SEARCH_DEBOUNCE_MS);
  });

  onMounted(async () => {
    await load(true);
    // The very first fetch right after a fresh LIFF launch can transiently
    // fail (the symptom: opening the archive from a 1:1 chat showed an empty
    // list until a tab-switch remount retried it). Auto-retry once so it
    // self-heals instead of resting on a misleading empty/error state — the
    // manual remount that "fixed" it was really just a second attempt.
    if (loadError.value) await load(true);
  });

  onUnmounted(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    clearTimeout(errorTimer);
    inFlight?.abort();
  });

  function toggleSelect(id: string) {
    if (selected.has(id)) {
      selected.delete(id);
      if (selected.size === 0) selectionMode.value = false; // deselecting the last item exits the mode (Photos behavior)
    } else {
      selected.add(id);
    }
  }

  function enterSelection(id: string) {
    selectionMode.value = true;
    selected.add(id);
  }

  function cancelSelection() {
    selected.clear();
    selectionMode.value = false;
  }

  async function deleteSelected() {
    if (selected.size === 0) return;
    const ids = [...selected];
    await runBusy("กำลังลบ...", async () => {
      await deleteItems(ids);
      dropFromPool(ids);
      cancelSelection();
      await load(true);
    });
  }

  async function restoreSelected() {
    if (selected.size === 0) return;
    const ids = [...selected];
    await runBusy("กำลังกู้คืน...", async () => {
      await restoreItems(ids);
      dropFromPool(ids);
      cancelSelection();
      await load(true);
    });
  }

  async function deleteOne(id: string) {
    await runBusy("กำลังลบ...", async () => {
      await deleteItems([id]);
      dropFromPool([id]);
      await load(true);
    });
  }

  async function restoreOne(id: string) {
    await runBusy("กำลังกู้คืน...", async () => {
      await restoreItems([id]);
      dropFromPool([id]);
      await load(true);
    });
  }

  async function download(item: ItemDTO) {
    // Downloads decrypt the full object server-side and stream it down —
    // seconds, not milliseconds, on mobile data. Deserves visible progress.
    await runBusy("กำลังดาวน์โหลด...", () => downloadItem(item.id, item.filename, item.mimeType));
  }

  // Reported items are hidden server-side rather than deleted (DESIGN.md §9.2)
  // — ItemDetailView calls reportItem() itself (same pattern as its direct
  // fetchItemObjectUrl call), then relies on this to drop the now-hidden item
  // out of whichever list — live or trash — is currently showing it.
  async function refresh() {
    await load(true);
  }

  /**
   * Paginating is only coherent against a server result: the cursor belongs to
   * the last query the server actually answered, so a local result set — which
   * may be showing a *different* query's matches — must not extend itself.
   */
  const hasMore = computed(() => resultScope.value === "server" && nextCursor.value !== null);

  /**
   * Rows are on screen, but only the ones this device had already paged in —
   * the server pass that would complete the answer is still owed or has failed.
   */
  const partialSearchResult = computed(
    () => query.value !== "" && resultScope.value === "local" && items.value.length > 0
  );

  /**
   * "The archive is bigger than what I could check instantly." Honest-about-limits
   * (PRODUCT.md): during this window the list is a correct but partial answer, and
   * saying so costs one quiet line — far better than a confident result count that
   * changes a moment later.
   */
  const searchingWholeArchive = computed(() => partialSearchResult.value && !loadError.value);

  /**
   * The same partial state, except the server pass errored — so the promise
   * "กำลังค้นต่อ..." is one the app already knows it won't keep. Left as-is, that
   * line span forever above a result set the user had no reason to distrust.
   * It's not the full-screen error state (there ARE real matches on screen); it's
   * the smaller, truer version of the same admission, with the way out attached.
   */
  const searchIncomplete = computed(() => partialSearchResult.value && !!loadError.value);

  const dateFilterActive = computed(() => filters.dateFrom !== "" || filters.dateTo !== "");

  const listState = computed<ListState>(() => {
    if (items.value.length > 0) return "ready";
    if (showSkeleton.value) return "skeleton";
    if (loadError.value) return "error";
    if (query.value) return searchPending.value || loading.value ? "searching" : "no-results";
    // A date range that matched nothing is not an archive with nothing in it.
    // Saying "ยังไม่มีไฟล์" here tells someone their files are gone when they
    // are one tap away, and hides the fact that a filter is even applied — the
    // chips row scrolls, so the active date chip may well be off-screen.
    if (dateFilterActive.value) return "filtered-empty";
    return "empty";
  });

  function clearSearch(): void {
    filters.q = "";
  }

  function clearDates(): void {
    filters.dateFrom = "";
    filters.dateTo = "";
  }

  return {
    items,
    filters,
    query,
    view,
    selected,
    selectionMode,
    busyLabel,
    errorLabel,
    loadError,
    loading,
    hasMore,
    listState,
    searchPending,
    searchingWholeArchive,
    searchIncomplete,
    dateFilterActive,
    clearSearch,
    clearDates,
    loadMore: () => load(false),
    retry: () => load(true),
    toggleSelect,
    enterSelection,
    cancelSelection,
    deleteSelected,
    restoreSelected,
    deleteOne,
    restoreOne,
    download,
    refresh,
  };
}
